import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Pets as PetsIcon,
  Home as HomeIcon,
  Share as ShareIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { PetProfile } from '../types/onboarding';
import { calculateAgeFromBirthday } from '../utils/birthdayCalculation';

interface PublicPetProfileProps {
  petId: string;
  userId: string;
}

export const PublicPetProfile: React.FC<PublicPetProfileProps> = ({ petId, userId }) => {
  const [pet, setPet] = useState<PetProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState<'en' | 'zh-CN' | 'es' | 'fr'>('en');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Language detection and content
  const detectLanguage = (text: string): 'en' | 'zh-CN' | 'es' | 'fr' => {
    if (/[\u4e00-\u9fff]/.test(text)) return 'zh-CN';
    if (/[áéíóúñü]/.test(text.toLowerCase())) return 'es';
    if (/[àâäéèêëïîôöùûüÿç]/.test(text.toLowerCase())) return 'fr';
    return 'en';
  };

  const getContent = () => {
    const content = {
      en: {
        title: "Pet Profile",
        backToSite: "Back to PetSnap",
        printProfile: "Print Profile",
        visitHomepage: "Visit PetSnap Homepage",
        introduction: "This pet profile is shared from PetSnap - Your AI-powered pet care assistant",
        poweredBy: "Powered by PetSnap",
        basicInfo: "Basic Information",
        healthInfo: "Health & Nutrition",
        careInfo: "Care Preferences",
        behaviorInfo: "Behavior & Daily Habits",
        age: "Age",
        gender: "Gender",
        breed: "Breed",
        weight: "Weight",
        activityLevel: "Activity Level",
        dietType: "Diet Type",
        specialNeeds: "Special Needs",
        temperament: "Temperament",
        trainingLevel: "Training Level",
        behavioralIssues: "Behavioral Issues",
        healthConditions: "Health Conditions",
        medications: "Medications",
        unknown: "Unknown",
        low: "Low",
        moderate: "Moderate",
        high: "High",
        male: "Male",
        female: "Female",
        unknownGender: "Unknown",
        loading: "Loading...",
        trainingLevelBeginner: "Beginner",
        trainingLevelIntermediate: "Intermediate",
        trainingLevelAdvanced: "Advanced",
        
        // Intake specific fields
        emergencyContact: "Emergency Contact",
        vetContact: "Veterinarian Contact",
        boardingDates: "Boarding Dates",
        foodAllergies: "Food Allergies",
        freshTreatPermission: "Fresh Treat Permission",
        medicalStatus: "Spayed / Neutered",
        pottyHabits: "Potty Habits",
        barkingHabits: "Barking / Vocal Habits",
        chewingHabits: "Chewing Habits",
        separationAnxiety: "Separation Anxiety",
        favoriteThings: "Favorite Things",
        walkingPreferences: "Walking Preferences",
        yes: "Yes",
        no: "No",
        allowed: "Allowed",
        notAllowed: "Not Allowed",
        to: "to"
      },
      'zh-CN': {
        title: "宠物资料",
        backToSite: "返回PetSnap",
        printProfile: "打印资料",
        visitHomepage: "访问PetSnap首页",
        introduction: "此宠物资料来自PetSnap - 您的AI宠物护理助手",
        poweredBy: "由PetSnap提供支持",
        basicInfo: "基本信息",
        healthInfo: "健康与营养",
        careInfo: "护理偏好",
        behaviorInfo: "行为与日常习惯",
        age: "年龄",
        gender: "性别",
        breed: "品种",
        weight: "体重",
        activityLevel: "活动水平",
        dietType: "饮食类型",
        specialNeeds: "特殊需求",
        temperament: "性格",
        trainingLevel: "训练水平",
        behavioralIssues: "行为问题",
        healthConditions: "健康状况",
        medications: "药物",
        unknown: "未知",
        low: "低",
        moderate: "中等",
        high: "高",
        male: "雄性",
        female: "雌性",
        unknownGender: "未知",
        loading: "加载中...",
        trainingLevelBeginner: "初学者",
        trainingLevelIntermediate: "中级",
        trainingLevelAdvanced: "高级",

        // Intake specific fields
        emergencyContact: "紧急联系人",
        vetContact: "兽医联系方式",
        boardingDates: "寄养日期",
        foodAllergies: "食物过敏",
        freshTreatPermission: "新鲜零食许可",
        medicalStatus: "绝育状态",
        pottyHabits: "排便习惯",
        barkingHabits: "吠叫/发声习惯",
        chewingHabits: "咀嚼习惯",
        separationAnxiety: "分离焦虑",
        favoriteThings: "最喜欢的事物",
        walkingPreferences: "散步偏好",
        yes: "是",
        no: "否",
        allowed: "允许",
        notAllowed: "不允许",
        to: "至"
      },
      es: {
        title: "Perfil de Mascota",
        backToSite: "Volver a PetSnap",
        printProfile: "Imprimir Perfil",
        visitHomepage: "Visitar Página Principal de PetSnap",
        introduction: "Este perfil de mascota es compartido desde PetSnap - Tu asistente de cuidado de mascotas con IA",
        poweredBy: "Desarrollado por PetSnap",
        basicInfo: "Información Básica",
        healthInfo: "Salud y Nutrición",
        careInfo: "Preferencias de Cuidado",
        behaviorInfo: "Comportamiento y Hábitos Diarios",
        age: "Edad",
        gender: "Género",
        breed: "Raza",
        weight: "Peso",
        activityLevel: "Nivel de Actividad",
        dietType: "Tipo de Dieta",
        specialNeeds: "Necesidades Especiales",
        temperament: "Temperamento",
        trainingLevel: "Nivel de Entrenamiento",
        behavioralIssues: "Problemas de Comportamiento",
        healthConditions: "Condiciones de Salud",
        medications: "Medicamentos",
        unknown: "Desconocido",
        low: "Bajo",
        moderate: "Moderado",
        high: "Alto",
        male: "Macho",
        female: "Hembra",
        unknownGender: "Desconocido",
        loading: "Cargando...",
        trainingLevelBeginner: "Principiante",
        trainingLevelIntermediate: "Intermedio",
        trainingLevelAdvanced: "Avanzado",

        // Intake specific fields
        emergencyContact: "Contacto de Emergencia",
        vetContact: "Contacto del Veterinario",
        boardingDates: "Fechas de Hospedaje",
        foodAllergies: "Alergias Alimentarias",
        freshTreatPermission: "Permiso de Premios Frescos",
        medicalStatus: "Castrado / Esterilizado",
        pottyHabits: "Hábitos de Ir al Baño",
        barkingHabits: "Hábitos de Ladrar / Vocalizar",
        chewingHabits: "Hábitos de Masticar",
        separationAnxiety: "Ansiedad por Separación",
        favoriteThings: "Cosas Favoritas",
        walkingPreferences: "Preferencias de Paseo",
        yes: "Sí",
        no: "No",
        allowed: "Permitido",
        notAllowed: "No Permitido",
        to: "a"
      },
      fr: {
        title: "Profil d'Animal",
        backToSite: "Retour à PetSnap",
        printProfile: "Imprimer le Profil",
        visitHomepage: "Visiter la Page d'Accueil de PetSnap",
        introduction: "Ce profil d'animal est partagé depuis PetSnap - Votre assistant de soins pour animaux alimenté par l'IA",
        poweredBy: "Alimenté par PetSnap",
        basicInfo: "Informations de Base",
        healthInfo: "Santé & Nutrition",
        careInfo: "Préférences de Soins",
        behaviorInfo: "Comportement & Habitudes Quotidiennes",
        age: "Âge",
        gender: "Genre",
        breed: "Race",
        weight: "Poids",
        activityLevel: "Niveau d'Activité",
        dietType: "Type de Régime",
        specialNeeds: "Besoins Spéciaux",
        temperament: "Tempérament",
        trainingLevel: "Niveau d'Entraînement",
        behavioralIssues: "Problèmes Comportementaux",
        healthConditions: "Conditions de Santé",
        medications: "Médicaments",
        unknown: "Inconnu",
        low: "Faible",
        moderate: "Modéré",
        high: "Élevé",
        male: "Mâle",
        female: "Femelle",
        unknownGender: "Inconnu",
        loading: "Chargement...",
        trainingLevelBeginner: "Débutant",
        trainingLevelIntermediate: "Intermédiaire",
        trainingLevelAdvanced: "Avancé",

        // Intake specific fields
        emergencyContact: "Contact d'Urgence",
        vetContact: "Contact du Vétérinaire",
        boardingDates: "Dates de Pension",
        foodAllergies: "Allergies Alimentaires",
        freshTreatPermission: "Permission pour Friandises Fraîches",
        medicalStatus: "Stérilisé / Castré",
        pottyHabits: "Habitudes d'Élimination",
        barkingHabits: "Habitudes d'Aboiement / Vocalisation",
        chewingHabits: "Habitudes de Mastication",
        separationAnxiety: "Anxiété de Séparation",
        favoriteThings: "Choses Préférées",
        walkingPreferences: "Préférences de Promenade",
        yes: "Oui",
        no: "Non",
        allowed: "Autorisé",
        notAllowed: "Non Autorisé",
        to: "à"
      }
    };

    return content[language] || content.en;
  };

  const content = getContent();

  // Load pet profile
  useEffect(() => {
    const loadPetProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const petRef = doc(db, 'users', userId, 'petProfiles', petId);
        const petSnap = await getDoc(petRef);

        if (petSnap.exists()) {
          const petData = petSnap.data() as PetProfile;
          setPet(petData);
          
          // Check for URL language override first, otherwise auto-detect
          const queryParams = new URLSearchParams(window.location.search);
          const urlLang = queryParams.get('lang');
          const validLanguages: Array<'en' | 'zh-CN' | 'es' | 'fr'> = ['en', 'zh-CN', 'es', 'fr'];
          
          if (urlLang && validLanguages.includes(urlLang as any)) {
            setLanguage(urlLang as any);
          } else {
            // Detect language from pet name and description
            const textToAnalyze = `${petData.name} ${petData.breed || ''} ${petData.aiAnalysisData?.description || ''}`;
            const detectedLang = detectLanguage(textToAnalyze);
            setLanguage(detectedLang);
          }
        } else {
          setError('Pet profile not found');
        }
      } catch (error) {
        console.error('Error loading pet profile:', error);
        setError('Failed to load pet profile');
      } finally {
        setLoading(false);
      }
    };

    if (petId && userId) {
      loadPetProfile();
    }
  }, [petId, userId]);

  const formatGender = (gender?: string): string => {
    const genderMap = {
      en: { male: content.male, female: content.female, unknown: content.unknownGender },
      'zh-CN': { male: content.male, female: content.female, unknown: content.unknownGender },
      es: { male: content.male, female: content.female, unknown: content.unknownGender },
      fr: { male: content.male, female: content.female, unknown: content.unknownGender }
    };
    return genderMap[language]?.[gender as keyof typeof genderMap.en] || content.unknownGender;
  };

  const formatActivityLevel = (level?: string): string => {
    const levelMap = {
      en: { low: content.low, moderate: content.moderate, high: content.high },
      'zh-CN': { low: content.low, moderate: content.moderate, high: content.high },
      es: { low: content.low, moderate: content.moderate, high: content.high },
      fr: { low: content.low, moderate: content.moderate, high: content.high }
    };
    return levelMap[language]?.[level as keyof typeof levelMap.en] || content.unknown;
  };

  const formatTrainingLevel = (level?: string): string => {
    const levelMap = {
      en: {
        beginner: content.trainingLevelBeginner,
        intermediate: content.trainingLevelIntermediate,
        advanced: content.trainingLevelAdvanced
      },
      'zh-CN': {
        beginner: content.trainingLevelBeginner,
        intermediate: content.trainingLevelIntermediate,
        advanced: content.trainingLevelAdvanced
      },
      es: {
        beginner: content.trainingLevelBeginner,
        intermediate: content.trainingLevelIntermediate,
        advanced: content.trainingLevelAdvanced
      },
      fr: {
        beginner: content.trainingLevelBeginner,
        intermediate: content.trainingLevelIntermediate,
        advanced: content.trainingLevelAdvanced
      }
    };
    return levelMap[language]?.[level as keyof typeof levelMap.en] || content.unknown;
  };

  const formatPetType = (type?: string): string => {
    if (!type) return content.unknown;
    
    const typeMap = {
      en: { dog: 'Dog', cat: 'Cat', other: 'Other' },
      'zh-CN': { dog: '狗', cat: '猫', other: '其他' },
      es: { dog: 'Perro', cat: 'Gato', other: 'Otro' },
      fr: { dog: 'Chien', cat: 'Chat', other: 'Autre' }
    };
    
    return typeMap[language]?.[type as keyof typeof typeMap.en] || type.toUpperCase();
  };

  const getCurrentAge = (pet: PetProfile): number => {
    if (pet.age) return pet.age;
    if (pet.birthday) {
      return calculateAgeFromBirthday(pet.birthday as any);
    }
    return 0;
  };

  const handleBackToSite = () => {
    window.location.href = window.location.origin;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={64} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            {content.loading || 'Loading...'}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !pet) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Pet profile not found'}
          </Alert>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={handleBackToSite}
          >
            {content.backToSite}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
        '@media print': {
          bgcolor: '#ffffff',
          py: 0,
          minHeight: 'auto'
        }
      }}
    >
      {/* Global CSS styles for printing to fit on one page */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          @page {
            size: portrait;
            margin: 6mm 10mm;
          }
          body {
            background: #ffffff !important;
            color: #000000 !important;
          }
          /* Ensure text and content fit nicely */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}} />

      <Container 
        maxWidth="md" 
        sx={{ 
          px: { xs: 2, sm: 3 },
          '@media print': {
            maxWidth: '100%',
            p: 0
          }
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            mb: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center' 
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 3, 
              flexWrap: 'wrap', 
              justifyContent: 'center',
              '@media print': {
                display: 'none'
              }
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToSite}
              size={isMobile ? 'medium' : 'large'}
            >
              {content.backToSite}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              size={isMobile ? 'medium' : 'large'}
            >
              {content.printProfile}
            </Button>
          </Box>
          
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            gutterBottom 
            color="primary"
            sx={{
              '@media print': {
                mb: 1
              }
            }}
          >
            {content.title}
          </Typography>
        </Box>

        {/* Pet Profile Card */}
        <Card 
          sx={{ 
            mb: 4, 
            boxShadow: 3,
            '@media print': {
              boxShadow: 'none',
              mb: 0,
              border: 'none'
            }
          }}
        >
          <CardContent 
            sx={{ 
              p: { xs: 2, sm: 3, md: 4 },
              '@media print': {
                p: 0
              }
            }}
          >
            {/* Pet Header */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 2, sm: 3 }, 
              mb: 4,
              flexDirection: isMobile ? 'column' : 'row',
              textAlign: isMobile ? 'center' : 'left',
              '@media print': {
                flexDirection: 'row',
                textAlign: 'left',
                mb: 2,
                gap: 2
              }
            }}>
              {pet.profilePhoto ? (
                <Avatar
                  src={pet.profilePhoto}
                  alt={pet.name}
                  sx={{ 
                    width: { xs: 100, sm: 120 }, 
                    height: { xs: 100, sm: 120 },
                    border: 3,
                    borderColor: 'primary.main'
                  }}
                />
              ) : (
                <Avatar sx={{ 
                  width: { xs: 100, sm: 120 }, 
                  height: { xs: 100, sm: 120 },
                  border: 3,
                  borderColor: 'primary.main',
                  bgcolor: 'primary.light'
                }}>
                  <PetsIcon sx={{ fontSize: { xs: 50, sm: 60 } }} />
                </Avatar>
              )}
              
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? 'h4' : 'h3'} gutterBottom sx={{ fontWeight: 'bold' }}>
                  {pet.name}
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  flexWrap: 'wrap', 
                  mb: 2,
                  justifyContent: isMobile ? 'center' : 'flex-start'
                }}>
                  <Chip 
                    label={formatPetType(pet.type)} 
                    color="primary" 
                    size="medium"
                  />
                  <Chip 
                    label={formatGender(pet.gender)} 
                    color="secondary" 
                    size="medium"
                  />
                  <Chip 
                    label={`${getCurrentAge(pet).toFixed(1)} ${content.age.toLowerCase()}`}
                    variant="outlined"
                    size="medium"
                  />
                </Box>

                <Typography variant="h6" color="text.secondary">
                  {(Array.isArray(pet.breed) ? pet.breed.join(', ') : pet.breed)?.trim() || content.unknown}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ 
              mb: 4,
              '@media print': {
                mb: 2
              }
            }} />

            {/* Pet Information Grid */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 3,
              '@media print': {
                gap: 2
              }
            }}>
              
              {/* Row 1: Basic Information & Health/Nutrition */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, 
                gap: 3,
                '@media print': {
                  flexDirection: 'row',
                  gap: 2
                }
              }}>
                {/* Basic Information Paper */}
                <Box sx={{ 
                  flex: 1,
                  '@media print': {
                    flex: '1 1 50%'
                  }
                }}>
                  <Paper variant="outlined" sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    height: '100%',
                    '@media print': {
                      p: 1.5
                    }
                  }}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                      {content.basicInfo}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {content.breed}
                        </Typography>
                        <Typography variant="body1">
                          {pet.breed || content.unknown}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {content.age} / {content.gender}
                        </Typography>
                        <Typography variant="body1">
                          {getCurrentAge(pet).toFixed(1)} {content.age.toLowerCase()} ({formatGender(pet.gender)})
                        </Typography>
                      </Box>

                      {pet.weight && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.weight}
                          </Typography>
                          <Typography variant="body1">
                            {pet.weight.value} {pet.weight.unit}
                          </Typography>
                        </Box>
                      )}

                      {pet.emergencyContact && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.emergencyContact}
                          </Typography>
                          <Typography variant="body1">
                            {pet.emergencyContact}
                          </Typography>
                        </Box>
                      )}

                      {pet.vetContact && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.vetContact}
                          </Typography>
                          <Typography variant="body1">
                            {pet.vetContact}
                          </Typography>
                        </Box>
                      )}

                      {pet.boardingDates && (pet.boardingDates.from || pet.boardingDates.to) && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.boardingDates}
                          </Typography>
                          <Typography variant="body1">
                            {pet.boardingDates.from || '?'} {content.to} {pet.boardingDates.to || '?'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Box>

                {/* Health & Nutrition Paper */}
                <Box sx={{ 
                  flex: 1,
                  '@media print': {
                    flex: '1 1 50%'
                  }
                }}>
                  <Paper variant="outlined" sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    height: '100%',
                    '@media print': {
                      p: 1.5
                    }
                  }}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                      {content.healthInfo}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {content.medicalStatus}
                        </Typography>
                        <Typography variant="body1">
                          {pet.medicalStatus?.isSpayedNeutered ? content.yes : content.no}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {content.foodAllergies}
                        </Typography>
                        <Typography variant="body1">
                          {pet.foodAllergies?.hasAllergies 
                            ? `${content.yes}${pet.foodAllergies.details ? `: ${pet.foodAllergies.details}` : ''}`
                            : content.no}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {content.freshTreatPermission}
                        </Typography>
                        <Typography variant="body1">
                          {pet.freshTreatPermission ? content.allowed : content.notAllowed}
                        </Typography>
                      </Box>

                      {pet.medications && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.medications}
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                            {pet.medications}
                          </Typography>
                        </Box>
                      )}

                      {pet.healthConditions && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.healthConditions}
                          </Typography>
                          <Typography variant="body1">
                            {pet.healthConditions}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Box>
              </Box>

              {/* Row 2: Behavior & Habits & Care Preferences */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, 
                gap: 3,
                '@media print': {
                  flexDirection: 'row',
                  gap: 2
                }
              }}>
                {/* Behavior & Habits Paper */}
                <Box sx={{ 
                  flex: 1,
                  '@media print': {
                    flex: '1 1 50%'
                  }
                }}>
                  <Paper variant="outlined" sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    height: '100%',
                    '@media print': {
                      p: 1.5
                    }
                  }}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                      {content.behaviorInfo}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {pet.pottyHabits && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.pottyHabits}
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                            {pet.pottyHabits}
                          </Typography>
                        </Box>
                      )}

                      {pet.barkingHabits && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.barkingHabits}
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                            {pet.barkingHabits}
                          </Typography>
                        </Box>
                      )}

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {content.chewingHabits}
                        </Typography>
                        <Typography variant="body1">
                          {pet.chewingHabits?.hasHabit
                            ? `${content.yes}${pet.chewingHabits.details ? `: ${pet.chewingHabits.details}` : ''}`
                            : content.no}
                        </Typography>
                      </Box>

                      {pet.separationAnxiety && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.separationAnxiety}
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                            {pet.separationAnxiety}
                          </Typography>
                        </Box>
                      )}

                      {(pet.temperament || pet.trainingLevel || pet.behavioralIssues) && <Divider sx={{ my: 1 }} />}

                      {pet.temperament && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.temperament}
                          </Typography>
                          <Typography variant="body1">
                            {pet.temperament}
                          </Typography>
                        </Box>
                      )}

                      {pet.trainingLevel && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {content.trainingLevel}
                          </Typography>
                          <Chip
                            label={formatTrainingLevel(pet.trainingLevel)}
                            size="small"
                            color="info"
                          />
                        </Box>
                      )}

                      {pet.behavioralIssues && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.behavioralIssues}
                          </Typography>
                          <Typography variant="body1">
                            {pet.behavioralIssues}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Box>

                {/* Favorites & Care Preferences Paper */}
                <Box sx={{ 
                  flex: 1,
                  '@media print': {
                    flex: '1 1 50%'
                  }
                }}>
                  <Paper variant="outlined" sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    height: '100%',
                    '@media print': {
                      p: 1.5
                    }
                  }}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                      {content.careInfo}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {pet.favoriteThings && pet.favoriteThings.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {content.favoriteThings}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {pet.favoriteThings.map((thing, idx) => (
                              <Chip key={idx} label={thing} size="small" variant="outlined" />
                            ))}
                          </Box>
                          {pet.favoriteThingsOther && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                              * {pet.favoriteThingsOther}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {pet.walkingPreferences && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.walkingPreferences}
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                            {pet.walkingPreferences}
                          </Typography>
                        </Box>
                      )}

                      {pet.activityLevel && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.activityLevel}
                          </Typography>
                          <Typography variant="body1">
                            {formatActivityLevel(pet.activityLevel)}
                          </Typography>
                        </Box>
                      )}

                      {pet.dietType && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {content.dietType}
                          </Typography>
                          <Typography variant="body1">
                            {pet.dietType}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Box>
              </Box>

              {/* Special Needs Section */}
              {pet.specialNeeds && (
                <Box sx={{ width: '100%' }}>
                  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'action.hover' }}>
                    <Typography variant="h6" gutterBottom color="error" sx={{ fontWeight: 'bold' }}>
                      {content.specialNeeds}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {pet.specialNeeds}
                    </Typography>
                  </Paper>
                </Box>
              )}

            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ 
          textAlign: 'center', 
          py: 3,
          '@media print': {
            display: 'none'
          }
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
            {content.introduction}
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 2, 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleBackToSite}
              size={isMobile ? 'medium' : 'large'}
              fullWidth={isMobile}
            >
              {content.visitHomepage}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={() => window.open(window.location.href, '_blank')}
              size={isMobile ? 'medium' : 'large'}
              fullWidth={isMobile}
            >
              {content.poweredBy}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicPetProfile;
